/**
 * Money core.
 *
 * MONEY RULE: a JS `number` NEVER holds a monetary amount. Amounts travel as
 * canonical decimal strings — optional leading "-", integer digits without
 * leading zeros, and (when the currency has a minor unit) "." followed by
 * exactly `exponent` fraction digits: "1234.50" (USD), "12.345" (KWD),
 * "1234" (JPY). Negative zero is never canonical.
 *
 * Formatting goes through `Intl.NumberFormat.formatToParts` on a lossless
 * reconstruction (BigInt integer part + fraction digits spliced in localized
 * form) so precision survives amounts beyond Number.MAX_SAFE_INTEGER.
 */

import { getCurrencyExponent, type CurrencyCode } from "./currencies";

export type MoneyValue = { amount: string; currency: string };

export type MoneyParseErrorCode =
  "invalid_characters" | "too_many_decimals" | "multiple_separators" | "empty";

export class MoneyParseError extends Error {
  readonly code: MoneyParseErrorCode;

  constructor(code: MoneyParseErrorCode, message?: string) {
    super(message ?? `money parse error: ${code}`);
    this.name = "MoneyParseError";
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Canonical form
// ---------------------------------------------------------------------------

/**
 * True when `amount` is in canonical form for `currency`: optional "-",
 * no leading zeros, exactly `exponent` fraction digits (none for exponent 0),
 * and not negative zero.
 */
export function isCanonical(amount: string, currency: CurrencyCode): boolean {
  const exponent = getCurrencyExponent(currency);
  const pattern =
    exponent > 0 ? new RegExp(`^-?(?:0|[1-9]\\d*)\\.\\d{${exponent}}$`) : /^-?(?:0|[1-9]\d*)$/;
  if (!pattern.test(amount)) return false;
  // Negative zero is not canonical.
  if (amount.startsWith("-") && !/[1-9]/.test(amount)) return false;
  return true;
}

/**
 * Normalizes already-split digits into the canonical string. Trailing fraction
 * zeros beyond the exponent are trimmed; non-zero excess precision throws.
 */
function assembleCanonical(
  negative: boolean,
  integerRaw: string,
  fractionRaw: string,
  exponent: number,
): string {
  let fraction = fractionRaw;
  if (fraction.length > exponent) {
    if (/[1-9]/.test(fraction.slice(exponent))) {
      throw new MoneyParseError("too_many_decimals");
    }
    fraction = fraction.slice(0, exponent);
  }
  fraction = fraction.padEnd(exponent, "0");
  let integer = integerRaw.replace(/^0+/, "");
  if (integer === "") integer = "0";
  const isZeroValue = !/[1-9]/.test(integer + fraction);
  const sign = negative && !isZeroValue ? "-" : "";
  return exponent > 0 ? `${sign}${integer}.${fraction}` : `${sign}${integer}`;
}

/**
 * Canonicalizes a plain ASCII decimal string ("12.5", "-7", ".5", "007.50")
 * for `currency`; e.g. canonicalizeAmount("12.5", "SAR") === "12.50".
 * Throws MoneyParseError for anything that is not a plain decimal — this is
 * for wire/programmatic values; user input goes through `parseMoneyInput`.
 */
export function canonicalizeAmount(raw: string, currency: CurrencyCode): string {
  const trimmed = raw.trim();
  if (trimmed === "") throw new MoneyParseError("empty");
  if (trimmed.split(".").length > 2) {
    throw new MoneyParseError("multiple_separators");
  }
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(trimmed);
  if (!match) throw new MoneyParseError("invalid_characters");
  const sign = match[1] ?? "";
  const integer = match[2] ?? "";
  const fraction = match[3] ?? "";
  if (integer === "" && fraction === "") {
    throw new MoneyParseError("invalid_characters");
  }
  return assembleCanonical(sign === "-", integer, fraction, getCurrencyExponent(currency));
}

interface SplitAmount {
  negative: boolean;
  /** Integer digits, unsigned. */
  integer: string;
  /** Fraction digits (may be ""). */
  fraction: string;
}

function splitAmount(amount: string): SplitAmount {
  const negative = amount.startsWith("-");
  const unsigned = negative ? amount.slice(1) : amount;
  const dot = unsigned.indexOf(".");
  return dot === -1
    ? { negative, integer: unsigned, fraction: "" }
    : { negative, integer: unsigned.slice(0, dot), fraction: unsigned.slice(dot + 1) };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export interface FormatMoneyOptions {
  /** "symbol" (default): currency symbol; "code": ISO code; "none": bare number. */
  display?: "symbol" | "code" | "none";
}

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(
  locale: string,
  currency: string,
  exponent: number,
  display: "symbol" | "code" | "none",
): Intl.NumberFormat {
  const key = `${locale}|${currency}|${exponent}|${display}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter =
      display === "none"
        ? new Intl.NumberFormat(locale, {
            style: "decimal",
            minimumFractionDigits: exponent,
            maximumFractionDigits: exponent,
          })
        : new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            currencyDisplay: display === "code" ? "code" : "symbol",
            minimumFractionDigits: exponent,
            maximumFractionDigits: exponent,
          });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

const localeDigitsCache = new Map<string, readonly string[]>();

/** Digits 0-9 rendered in the locale's default numbering system. */
function getLocaleDigits(locale: string): readonly string[] {
  let digits = localeDigitsCache.get(locale);
  if (!digits) {
    const formatter = new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 0,
    });
    digits = Array.from({ length: 10 }, (_, i) => formatter.format(i));
    localeDigitsCache.set(locale, digits);
  }
  return digits;
}

/**
 * Locale-aware money formatting without ever putting the amount in a JS
 * number: the integer part is formatted as a BigInt (lossless at any length)
 * and the exact fraction digits are spliced back in, transliterated to the
 * locale's numbering system.
 */
/**
 * Format an amount at its own scale.
 *
 * A currency's exponent is what its smallest coin is, not what a ledger stores:
 * a depreciation total carried at four decimals is exact, and rounding it for
 * display would show a number the books do not contain. This keeps every digit
 * the amount has and formats the rest — grouping, sign placement, numbering
 * system — the way the reader's locale does.
 */
export function formatExactMoney(
  value: MoneyValue,
  locale: string,
  options: FormatMoneyOptions = {},
): string {
  const display = options.display ?? "symbol";
  const { negative, integer, fraction } = splitAmount(value.amount.trim());
  const wellFormedCurrency = /^[A-Za-z]{3}$/.test(value.currency);
  const formatter = getFormatter(
    locale,
    wellFormedCurrency ? value.currency.toUpperCase() : "USD",
    fraction.length,
    wellFormedCurrency ? display : "none",
  );
  const integerBig = BigInt(integer === "" ? "0" : integer);
  const target: number | bigint = negative ? (integerBig === 0n ? -0 : -integerBig) : integerBig;
  const parts = formatter.formatToParts(target);
  let localizedFraction: string | null = null;
  if (fraction.length > 0) {
    const digits = getLocaleDigits(locale);
    localizedFraction = Array.from(fraction, (d) => digits[d.charCodeAt(0) - 48] ?? d).join("");
  }
  const formatted = parts
    .map((part) =>
      part.type === "fraction" && localizedFraction !== null ? localizedFraction : part.value,
    )
    .join("");
  if (display !== "none" && !wellFormedCurrency) {
    return `${formatted}\u00A0${value.currency}`;
  }
  return formatted;
}

export function formatMoney(
  value: MoneyValue,
  locale: string,
  options: FormatMoneyOptions = {},
): string {
  const display = options.display ?? "symbol";
  const exponent = getCurrencyExponent(value.currency);
  const canonical = isCanonical(value.amount, value.currency)
    ? value.amount
    : canonicalizeAmount(value.amount, value.currency);
  const { negative, integer, fraction } = splitAmount(canonical);

  // Intl throws on codes that are not three ASCII letters; fall back to a
  // bare number with the raw code appended rather than crashing the UI.
  const wellFormedCurrency = /^[A-Za-z]{3}$/.test(value.currency);
  const effectiveDisplay = wellFormedCurrency ? display : "none";
  const formatter = getFormatter(locale, value.currency.toUpperCase(), exponent, effectiveDisplay);

  const integerBig = BigInt(integer);
  // BigInt cannot represent -0; for "-0.xx" format the number -0, which is
  // exact and keeps the locale's sign placement.
  const target: number | bigint = negative ? (integerBig === 0n ? -0 : -integerBig) : integerBig;
  const parts = formatter.formatToParts(target);

  let localizedFraction: string | null = null;
  if (exponent > 0) {
    const digits = getLocaleDigits(locale);
    localizedFraction = Array.from(fraction, (d) => digits[d.charCodeAt(0) - 48] ?? d).join("");
  }

  const formatted = parts
    .map((part) =>
      part.type === "fraction" && localizedFraction !== null ? localizedFraction : part.value,
    )
    .join("");
  if (display !== "none" && !wellFormedCurrency) {
    return `${formatted}\u00A0${value.currency}`;
  }
  return formatted;
}

// ---------------------------------------------------------------------------
// Parsing user input
// ---------------------------------------------------------------------------

interface LocaleSeparators {
  decimal: string;
  group: string;
}

const separatorCache = new Map<string, LocaleSeparators>();

function getLocaleSeparators(locale: string): LocaleSeparators {
  let separators = separatorCache.get(locale);
  if (!separators) {
    const parts = new Intl.NumberFormat(locale).formatToParts(1234567.8);
    separators = {
      decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
      group: parts.find((p) => p.type === "group")?.value ?? ",",
    };
    separatorCache.set(locale, separators);
  }
  return separators;
}

/** Maps Western, Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹) digits to ASCII. */
function normalizeDigit(ch: string): string | null {
  const code = ch.codePointAt(0) ?? 0;
  if (code >= 0x30 && code <= 0x39) return ch;
  if (code >= 0x0660 && code <= 0x0669) return String.fromCharCode(code - 0x0660 + 0x30);
  if (code >= 0x06f0 && code <= 0x06f9) return String.fromCharCode(code - 0x06f0 + 0x30);
  return null;
}

function classifySeparator(
  ch: string,
  localeDecimal: string,
  localeGroup: string,
): "decimal" | "group" | null {
  if (ch === "٫") return "decimal"; // Arabic decimal separator ٫
  if (ch === "٬") return "group"; // Arabic thousands separator ٬
  if (ch === localeDecimal) return "decimal";
  if (ch === localeGroup) return "group";
  // Western separators typed under a locale whose own separators are
  // non-ASCII (e.g. Arabic): "." is the decimal unless the locale uses a
  // comma-decimal convention, in which case the roles flip.
  if (ch === ".") return localeDecimal === "," ? "group" : "decimal";
  if (ch === ",") return localeDecimal === "," ? "decimal" : "group";
  return null;
}

/**
 * Bidi formatting/control characters that keyboards and copy-paste inject
 * around numbers: ALM, LRM/RLM, LRE..PDF embeddings, LRI..PDI isolates.
 */
const BIDI_CONTROLS_RE = new RegExp("[\\u061C\\u200E\\u200F\\u202A-\\u202E\\u2066-\\u2069]", "g");

/**
 * Parses free-form user input into a canonical amount string for `currency`.
 *
 * Accepts Western and Arabic-Indic/Extended digits, Arabic (٫ ٬) and Western
 * ("." ",") separators — the Western pair disambiguated by `locale` — ASCII
 * "-" / Unicode minus, and ignores all whitespace (incl. NBSP/NNBSP) and bidi
 * marks. Throws MoneyParseError with a machine `code` on anything else.
 */
export function parseMoneyInput(raw: string, currency: CurrencyCode, locale: string): string {
  const exponent = getCurrencyExponent(currency);
  const cleaned = raw.replace(BIDI_CONTROLS_RE, "").replace(/\s/g, "");
  if (cleaned === "") throw new MoneyParseError("empty");

  const { decimal: localeDecimal, group: localeGroup } = getLocaleSeparators(locale);

  let negative = false;
  let sawSign = false;
  let sawDecimal = false;
  let sawContent = false;
  let integerDigits = "";
  let fractionDigits = "";

  for (const ch of cleaned) {
    const digit = normalizeDigit(ch);
    if (digit !== null) {
      sawContent = true;
      if (sawDecimal) fractionDigits += digit;
      else integerDigits += digit;
      continue;
    }
    if (ch === "-" || ch === "−") {
      // Sign must lead (bidi marks around it are already stripped).
      if (sawSign || sawContent) throw new MoneyParseError("invalid_characters");
      sawSign = true;
      negative = true;
      continue;
    }
    const role = classifySeparator(ch, localeDecimal, localeGroup);
    if (role === "decimal") {
      if (sawDecimal) throw new MoneyParseError("multiple_separators");
      sawDecimal = true;
      sawContent = true;
      continue;
    }
    if (role === "group") {
      if (sawDecimal) throw new MoneyParseError("invalid_characters");
      sawContent = true;
      continue;
    }
    throw new MoneyParseError("invalid_characters");
  }

  if (integerDigits === "" && fractionDigits === "") {
    throw new MoneyParseError("empty");
  }
  return assembleCanonical(negative, integerDigits, fractionDigits, exponent);
}

// ---------------------------------------------------------------------------
// Comparison & sign helpers (string-safe — no floats, ever)
// ---------------------------------------------------------------------------

function splitForCompare(amount: string): SplitAmount {
  if (!/^-?\d+(?:\.\d+)?$/.test(amount)) {
    throw new MoneyParseError("invalid_characters", `not a decimal amount: "${amount}"`);
  }
  return splitAmount(amount);
}

/** Compares |a| and |b| digit-wise; both integer parts are unsigned digit strings. */
function compareMagnitude(a: SplitAmount, b: SplitAmount): -1 | 0 | 1 {
  const intA = a.integer.replace(/^0+/, "");
  const intB = b.integer.replace(/^0+/, "");
  if (intA.length !== intB.length) return intA.length < intB.length ? -1 : 1;
  if (intA !== intB) return intA < intB ? -1 : 1;
  const width = Math.max(a.fraction.length, b.fraction.length);
  const fracA = a.fraction.padEnd(width, "0");
  const fracB = b.fraction.padEnd(width, "0");
  if (fracA !== fracB) return fracA < fracB ? -1 : 1;
  return 0;
}

/**
 * Decimal string comparison: returns -1 | 0 | 1. Tolerates non-canonical but
 * well-formed inputs ("1.5" vs "1.50", "-0.00"). Amounts must share a
 * currency for the comparison to be meaningful — this compares numbers only.
 */
export function compareAmounts(a: string, b: string): -1 | 0 | 1 {
  const partsA = splitForCompare(a);
  const partsB = splitForCompare(b);
  const signA = isZero(a) ? 0 : partsA.negative ? -1 : 1;
  const signB = isZero(b) ? 0 : partsB.negative ? -1 : 1;
  if (signA !== signB) return signA < signB ? -1 : 1;
  if (signA === 0) return 0;
  const magnitude = compareMagnitude(partsA, partsB);
  if (magnitude === 0) return 0;
  if (signA === 1) return magnitude;
  return magnitude === 1 ? -1 : 1;
}

/** True when the amount is zero ("0", "0.00", "-0.000"). */
export function isZero(amount: string): boolean {
  return /^-?0+(?:\.0+)?$/.test(amount);
}

/** Flips the sign of a decimal amount string; zero stays positive. */
export function negateAmount(amount: string): string {
  if (isZero(amount)) return amount.startsWith("-") ? amount.slice(1) : amount;
  return amount.startsWith("-") ? amount.slice(1) : `-${amount}`;
}

/** Absolute value of a decimal amount string. */
export function absAmount(amount: string): string {
  return amount.startsWith("-") ? amount.slice(1) : amount;
}

export function negate(value: MoneyValue): MoneyValue {
  return { ...value, amount: negateAmount(value.amount) };
}

export function abs(value: MoneyValue): MoneyValue {
  return { ...value, amount: absAmount(value.amount) };
}

/** Canonical zero for a currency, e.g. "0.00" (USD), "0.000" (KWD), "0" (JPY). */
export function zeroAmount(currency: CurrencyCode): string {
  return assembleCanonical(false, "0", "", getCurrencyExponent(currency));
}
